import type {
  Cuisine,
  DietaryOption,
  ProfileGap,
  ServiceArea,
  ServiceType,
  SupplierCertification,
  SupplierStatus,
} from '@caterinx/shared-types';

import { api } from './api.js';

/**
 * Der API-Zugang des Lieferantenportals zu den eigenen Stammdaten.
 *
 * **Die Typen kommen aus `@caterinx/shared-types`, nicht aus einer Kopie hier.** Das ist der
 * ganze Zweck des Pakets: Wenn im Backend eine Leistungsart dazukommt, bricht hier der Build
 * — statt dass die Oberfläche sie stillschweigend nicht anbietet.
 */

export interface SupplierProfileResponse {
  supplierId: string;
  companyName: string;
  status: SupplierStatus;
  statusReason: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  address: { street: string; postalCode: string; city: string; country: string };
  description: string;
  logoS3Key: string | null;
  logoUrl: string | null;
  referenceImageS3Keys: string[];
  referenceImageUrls: string[];
  websiteUrl: string | null;
  serviceTypes: ServiceType[];
  cuisines: Cuisine[];
  dietaryOptions: DietaryOption[];
  certifications: SupplierCertification[];
  serviceAreas: ServiceArea[];
  /** Liefert deutschlandweit. Standard bei der Registrierung — siehe `AreasStep`. */
  nationwide: boolean;
  minOrderNetCents: number;
  minGuests: number;
  maxGuests: number | null;
  /** In Stunden gespeichert, im Formular in Tagen erfasst (`leadTimeDaysFromHours`). */
  leadTimeHours: number;
  availability: { blackoutDates: string[] };
  billing: {
    billingEmail: string | null;
    billingName: string | null;
    billingAddress: { street: string; postalCode: string; city: string; country: string } | null;
  };
  compliance: {
    vatId: string | null;
    taxNumber: string | null;
    tradeRegisterVerifiedAt: string | null;
    vatIdVerifiedAt: string | null;
    foodHygieneVerifiedAt: string | null;
    documents: {
      tradeRegisterS3Key: string | null;
      foodHygieneS3Key: string | null;
      liabilityInsuranceS3Key: string | null;
    };
  };
  documentUrls: {
    tradeRegisterUrl: string | null;
    foodHygieneUrl: string | null;
    liabilityInsuranceUrl: string | null;
  };
  commissionRateBp: number | null;
  /** Was noch fehlt — dieselbe Liste, an der die Freischaltung im Admin scheitert. */
  missingFields: ProfileGap[];
  createdAt: string;
  updatedAt: string;
}

export type UploadKind =
  | 'logo'
  | 'reference'
  | 'trade_register'
  | 'food_hygiene'
  | 'liability_insurance'
  /** Menüvorschlag zu einem Angebot (Phase 5) — derselbe Endpunkt, derselbe Präfix. */
  | 'offer_attachment';

export async function fetchMyProfile(): Promise<SupplierProfileResponse> {
  const { data } = await api.get<SupplierProfileResponse>('/suppliers/me');
  return data;
}

/** Teilmengen sind der Regelfall: Der Wizard speichert nach jedem Schritt. */
export async function saveProfile(patch: Record<string, unknown>): Promise<void> {
  await api.put('/suppliers/me', patch);
}

/**
 * Liefergebiete **und** die Deutschlandweit-Entscheidung in einem Aufruf.
 *
 * Beides gehört zusammen: Hinter „deutschlandweit" steht ein eigenes Gebiets-Item
 * (`AREA#ALL`), das der Server in derselben Transaktion schreibt oder löscht. Zwei Aufrufe
 * hätten ein Zeitfenster, in dem der Betrieb weder bundesweit noch regional liefert.
 */
export async function saveServiceAreas(serviceAreas: ServiceArea[], nationwide: boolean): Promise<void> {
  await api.put('/suppliers/me/areas', { serviceAreas, nationwide });
}

export async function saveDocuments(patch: {
  vatId?: string | null;
  taxNumber?: string | null;
  tradeRegisterS3Key?: string | null;
  foodHygieneS3Key?: string | null;
  liabilityInsuranceS3Key?: string | null;
}): Promise<void> {
  await api.put('/suppliers/me/documents', patch);
}

export async function submitForReview(): Promise<void> {
  await api.post('/suppliers/me/submit-for-review', {});
}

/**
 * Zweistufiger Upload: erst eine vorsignierte URL holen, dann **direkt** nach S3 schicken.
 *
 * Der zweite Schritt läuft bewusst über `fetch` und nicht über die Axios-Instanz: Die
 * trägt den Auth-Interceptor und würde einen `Authorization`-Header an S3 senden. S3 wertet
 * den aus — und lehnt die Anfrage ab, weil er nicht zur Signatur in der URL passt.
 *
 * Der `Content-Type` muss **exakt** dem entsprechen, der beim Signieren angegeben wurde;
 * er ist Teil der Signatur.
 */
export async function uploadFile(kind: UploadKind, file: File): Promise<string> {
  const { data } = await api.post<{ uploadUrl: string; s3Key: string }>('/suppliers/me/uploads', {
    kind,
    contentType: file.type,
  });

  const response = await fetch(data.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });

  if (!response.ok) {
    throw new Error(`Upload fehlgeschlagen (${response.status})`);
  }

  return data.s3Key;
}

/* --------------------------------------------------------------------------------------------
 * Team
 * ----------------------------------------------------------------------------------------- */

export interface TeamMember {
  userId: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'invited';
  createdAt: string;
  isSelf: boolean;
  expiresAt?: string;
}

export async function fetchTeam(): Promise<{ members: TeamMember[]; canManage: boolean }> {
  const { data } = await api.get<{ members: TeamMember[]; canManage: boolean }>('/suppliers/me/team');
  return data;
}

export async function inviteTeamMember(input: {
  email: string;
  name?: string;
  role: 'admin' | 'member';
}): Promise<void> {
  await api.post('/suppliers/me/team/invites', input);
}

/**
 * Entfernt einen Mitarbeiter **oder** zieht eine offene Einladung zurück — derselbe Endpunkt,
 * unterschieden am Bezug: Cognito-`sub` oder E-Mail-Adresse.
 */
export async function removeTeamMember(memberRef: string): Promise<void> {
  await api.delete(`/suppliers/me/team/${encodeURIComponent(memberRef)}`);
}

/* --------------------------------------------------------------------------------------------
 * Darstellung
 * ----------------------------------------------------------------------------------------- */

/** Cent → „1.250,00 EUR". Anzeige-Formatierung, **nie** Rechnen (Goldene Regel 3). */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/** Eingabe in Euro (auch mit Komma) → Integer-Cent. */
export function parseEuroToCents(value: string): number {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  const euros = Number(normalized);
  if (!Number.isFinite(euros) || euros < 0) return 0;
  return Math.round(euros * 100);
}
